import { FC, useContext, useState } from "react";
import { Box, createStyles, makeStyles, Typography } from "@material-ui/core";
import { Snackbar } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { FormikHelpers, useFormik } from "formik";
import { startCase } from "lodash";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";

import { Button, DataDisclosureMessage } from "components";
import OutlinedTextField from "components/OutlinedTextField";
import * as addRoyaltyAccountRoutes from "containers/AddRoyaltyAccount/routes";
import { AppContext } from "contexts";
import { createAssetKYWCredentialSubmission } from "services/assets";
import { RoyaltyPayor } from "services/royaltyPayor/types";
import { getCurrentUser } from "services/users";
import { textFieldPurpleStyles } from "theme/constants";

import TimeForEstimation from "../TimeForEstimation";

const useStyles = makeStyles(() =>
  createStyles({
    inputRoot: textFieldPurpleStyles,
  }),
);

interface ShareYourCredentialsProps {
  royaltyPayor: RoyaltyPayor;
}

interface FormValues {
  username: string;
  password: string;
}

interface FormValidation {
  open: boolean;
  message?: string;
}

const ShareYourCredentials: FC<ShareYourCredentialsProps> = ({
  royaltyPayor,
}) => {
  const history = useHistory();
  const classes = useStyles();
  const [snackbarConfig, setSnackbarConfig] = useState<FormValidation>({
    open: false,
  });

  const { setUser } = useContext(AppContext);
  const handleConnectAccount = async (
    formValues: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>,
  ) => {
    try {
      setSubmitting(true);
      const asset = await createAssetKYWCredentialSubmission({
        username: formValues.username,
        password: formValues.password,
        royalty_payor_id: royaltyPayor.name,
      });
      // once the asset is created we need to update the user object in order to update `canSeeSellerDashboard`
      setUser(await getCurrentUser());
      history.push(
        addRoyaltyAccountRoutes.connectGetCredentialsRoyaltyPayorAccountDetails(
          royaltyPayor.name,
          asset.id.toString(),
        ),
      );
    } catch (e) {
      {
        const error = e as Error;
        setSnackbarConfig({
          open: true,
          message: error.message,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const initialValues = {
    username: "",
    password: "",
  };
  const formik = useFormik<FormValues>({
    onSubmit: handleConnectAccount,
    validationSchema: Yup.object().shape({
      username: Yup.string()
        .max(128)
        .label(startCase(royaltyPayor.username_or_email ?? "username"))
        .required("Required"),
      password: Yup.string().required("Required"),
    }),
    initialValues,
  });
  return (
    <>
      <Box mb={2}>
        <TimeForEstimation
          royaltyPayorName={royaltyPayor.display_name}
          hasGetCredentialPermission
        />
      </Box>
      <Box my={2}>
        <Typography color="primary" align="left">
          Connect your {royaltyPayor.display_name} account by entering your
          username and password exactly as you would on the{" "}
          {royaltyPayor.display_name} site.
        </Typography>
      </Box>
      {!!royaltyPayor.credentials_helper_text &&
        royaltyPayor.credentials_helper_text
          .split(/(\r\n|\r|\n)/)
          .filter(i => !["\r\n", "\r", "\n"].includes(i))
          .map((paragraph, i) => (
            <Typography color="primary" key={i}>
              {paragraph}
            </Typography>
          ))}
      <Typography color="primary" align="left">
        You’ll give our system temporary access to your{" "}
        {royaltyPayor.display_name} account where it can read statements and
        provide an analysis of your royalties.
      </Typography>
      <form onSubmit={formik.handleSubmit}>
        <Box my={2} textAlign="left">
          <OutlinedTextField
            data-attr="credentials-submission-username"
            className={classes.inputRoot}
            label={startCase(royaltyPayor.username_or_email ?? "username")}
            name="username"
            value={formik.values.username}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.username && !!formik.errors.username}
            helperText={formik.touched.username && formik.errors.username}
            disabled={formik.isSubmitting}
            autoFocus
          />
        </Box>
        <Box my={2} textAlign="left">
          <OutlinedTextField
            data-attr="credentials-submission-password"
            className={classes.inputRoot}
            label="Password"
            name="password"
            type="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && !!formik.errors.password}
            helperText={formik.touched.password && formik.errors.password}
            disabled={formik.isSubmitting}
          />
          <Box mt={1}>
            <DataDisclosureMessage />
          </Box>
        </Box>
        <Box display="flex" flexDirection="row-reverse" width="100%">
          <Button
            data-attr="credentials-submission-submit-credentials-button"
            customColor="green"
            variant="contained"
            disabled={formik.isSubmitting || !formik.isValid}
            type="submit"
            isLoading={formik.isSubmitting}
            style={{ width: "179px" }}
          >
            {formik.isSubmitting ? "Submitting" : "Connect Account"}
          </Button>
        </Box>
      </form>
      <Snackbar
        open={snackbarConfig.open}
        autoHideDuration={3000}
        onClose={() => setSnackbarConfig({ open: false })}
      >
        <Alert
          onClose={() => setSnackbarConfig({ open: false })}
          severity="error"
        >
          {snackbarConfig.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ShareYourCredentials;
